import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout as logoutAction } from "../../features/auth/store/authSlice";
import apiClient from "../../core/config/api";
import type { CountryCode } from "libphonenumber-js";
import CountryCodeSelect from "../../components/phone/CountryCodeSelect";
import PhoneVerificationModal from "../../components/phone/PhoneVerificationModal";
import { formatNational, formatPhone, maskedExample, splitPhone, toE164 } from "../../components/phone/phoneUtils";
import "./my-accunt.css";

interface UserProfile {
    first_name?: string;
    last_name?: string;
    email?: string;
    job_title?: string;
    phone_number?: string;
    last_profile_update?: string;
    account_status?: string;
    profile_image_url?: string;
    terms_and_condition?: boolean;
    social_media?: UserSocialMedia;
    organization?: UserOrganization;
}

interface UserSocialMedia {
    linkedin_url?: string;
    twitter_url?: string;
    facebook_url?: string;
    instagram_url?: string;
}

interface UserOrganization {
    company_name?: string;
    website_address?: string;
    industry?: string;
    number_of_employees?: string;
    annual_revenue?: string;
    company_address?: string;
    state?: string;
    city?: string;
    postal_code?: string;
}

interface SecurityActivity {
    date: string;
    activity: string;
    location: string;
    device: string;
}

const MyAccount = () => {
    
    const [activeTab, setActiveTab] = useState("profile");
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [profileForm, setProfileForm] = useState<UserProfile>({});
    const [termsAndCondition, setTermsAndCondition] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [profileMessage, setProfileMessage] = useState<string | null>(null);
    // Phone number: country + national digits; `phoneDirty` once edited.
    // Free plan users' new numbers are saved only after the SMS code checks
    // out (Bird Verify); everyone else's are saved straight away, unverified.
    const [phoneCountry, setPhoneCountry] = useState<CountryCode>(() => splitPhone(null).country);
    const [phoneNational, setPhoneNational] = useState("");
    const [phoneDirty, setPhoneDirty] = useState(false);
    const [phoneStatus, setPhoneStatus] = useState<{
        applies: boolean;
        phone_verified: boolean;
        pending_phone_number: string | null;
    } | null>(null);
    const [phoneModal, setPhoneModal] = useState<{ purpose: "gate" | "change"; phone?: string } | null>(null);
    const [passwordMessage, setPasswordMessage] = useState<string | null>(null);

    const [open, setOpen] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [socialMedia, setSocialMedia] = useState<UserSocialMedia>({});
    const [organization, setOrganization] = useState<UserOrganization>({});
    const [saving, setSaving] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [pwdSaving, setPwdSaving] = useState(false);
    const [, setPwdCurrent] = useState("");
    const [pwdNew, setPwdNew] = useState("");
    const [pwdConfirm, setPwdConfirm] = useState("");
    const [securityActivities, setSecurityActivities] = useState<SecurityActivity[]>([]);
    const [securityActivityLoading, setSecurityActivityLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const popupRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch user profile data
    useEffect(() => {
        const fetchUserProfile = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.get("/account/re/", {
                    params: { _t: Date.now() },
                });
                setUserProfile(response.data);
                setProfileForm({
                    first_name: response.data?.first_name || "",
                    last_name: response.data?.last_name || "",
                    job_title: response.data?.job_title || "",
                    phone_number: response.data?.phone_number || "",
                });
                setTermsAndCondition(Boolean(response.data?.terms_and_condition));
                setSocialMedia(response.data?.social_media || {});
                setOrganization(response.data?.organization || {});
                resetPhoneField(response.data?.phone_number);
                loadPhoneStatus();
            } catch (error: any) {
                setError("Failed to load profile data from API. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserProfile();
    }, []);

    // Fetch security activity when security tab is active
    useEffect(() => {
        if (activeTab === "security") {
            const fetchSecurityActivity = async () => {
                setSecurityActivityLoading(true);
                try {
                    const response = await apiClient.get("/account/re/security-activity");
                    setSecurityActivities(response.data.activities || []);
                } catch (error: any) {
                    setSecurityActivities([]);
                }
                setSecurityActivityLoading(false);
            };
            fetchSecurityActivity();
        }
    }, [activeTab]);

    const fileToBase64 = (file: File) =>
        new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result?.toString();
                if (!result) {
                    reject(new Error("Failed to read file"));
                    return;
                }
                resolve(result);
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsDataURL(file);
        });

    const handleFile = (file: File | null) => {
        if (!file) return;
        const isValidType = ["image/jpeg", "image/png"].includes(file.type);
        const isValidSize = file.size <= 5 * 1024 * 1024;
        if (!isValidType) {
            setError("Please upload a JPG or PNG image");
            return;
        }
        if (!isValidSize) {
            setError("File must be 5MB or smaller");
            return;
        }
        setError(null);
        setSelectedFile(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragActive(false);
        handleFile(e.dataTransfer.files[0]);
    };

    const handleRemoveImage = async () => {
        try {
            setImageUploading(true);
            await apiClient.put("/account/re/profile-image", { image_base64: null });
            setUserProfile((prev) => (prev ? { ...prev, profile_image_url: undefined } : prev));
            setSelectedFile(null);
            window.dispatchEvent(new CustomEvent("profileImageUpdated"));
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Failed to remove profile image");
        } finally {
            setImageUploading(false);
        }
    };

    const previewImage = () => {
        if (selectedFile) {
            return URL.createObjectURL(selectedFile);
        }
        if (userProfile?.profile_image_url) return userProfile.profile_image_url;
        return "/images/popup-img.png";
    };

    const handleSave = async () => {
        if (!selectedFile) return;
        try {
            setImageUploading(true);
            const base64 = await fileToBase64(selectedFile);
            const response = await apiClient.put("/account/re/profile-image", { image_base64: base64 });
            setUserProfile((prev) => (prev ? { ...prev, profile_image_url: response.data.profile_image_url } : prev));
            setOpen(false);
            setSelectedFile(null);
            window.dispatchEvent(new CustomEvent("profileImageUpdated"));
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Failed to upload profile image");
        } finally {
            setImageUploading(false);
        }
    };

    const handleSocialChange = (key: keyof UserSocialMedia, value: string) => {
        setSocialMedia((prev) => ({ ...prev, [key]: value }));
    };

    const handleOrgChange = (key: keyof UserOrganization, value: string) => {
        setOrganization((prev) => ({ ...prev, [key]: value }));
    };

    const resetPhoneField = (phone?: string | null) => {
        const parts = splitPhone(phone);
        setPhoneCountry(parts.country);
        setPhoneNational(parts.national);
        setPhoneDirty(false);
    };

    const loadPhoneStatus = async () => {
        try {
            const { data } = await apiClient.get("/account/re/phone/status");
            setPhoneStatus({
                applies: !!data?.applies,
                phone_verified: !!data?.phone_verified,
                pending_phone_number: data?.pending_phone_number || null,
            });
        } catch {
            setPhoneStatus(null);
        }
    };

    const refreshProfilePhone = async () => {
        const refreshed = await apiClient.get("/account/re/", { params: { _t: Date.now() } });
        setUserProfile(refreshed.data);
        setProfileForm((prev) => ({ ...prev, phone_number: refreshed.data?.phone_number || "" }));
        resetPhoneField(refreshed.data?.phone_number);
        await loadPhoneStatus();
    };

    const discardPendingPhone = async () => {
        try {
            await apiClient.delete("/account/re/phone/pending");
        } finally {
            await loadPhoneStatus();
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        setError(null);
        setProfileMessage(null);
        try {
            // The phone number goes with the profile only when it was edited.
            let newPhone: string | null | undefined;
            if (phoneDirty) {
                if (!phoneNational.trim()) {
                    newPhone = "";
                } else {
                    newPhone = toE164(phoneCountry, phoneNational);
                    if (!newPhone) {
                        setError("Enter a valid mobile number for the selected country.");
                        setSaving(false);
                        return;
                    }
                }
            }
            const current = userProfile?.phone_number || "";
            // Free plan: a new number is verified by SMS code before it's saved.
            const needsCode = !!phoneStatus?.applies && !!newPhone && newPhone !== current;

            await apiClient.put("/account/", {
                first_name: profileForm.first_name,
                last_name: profileForm.last_name,
                job_title: profileForm.job_title,
                ...(newPhone !== undefined && !needsCode ? { phone_number: newPhone } : {}),
                terms_and_condition: termsAndCondition,
            });
            if (needsCode && newPhone) setPhoneModal({ purpose: "change", phone: newPhone });

            await apiClient.put("/account/re/social-media", socialMedia);
            await apiClient.put("/account/re/organization", organization);
            const refreshed = await apiClient.get("/account/re/", { params: { _t: Date.now() } });
            setUserProfile(refreshed.data);
            setProfileForm({
                first_name: refreshed.data?.first_name || "",
                last_name: refreshed.data?.last_name || "",
                job_title: refreshed.data?.job_title || "",
                phone_number: refreshed.data?.phone_number || "",
            });
            setTermsAndCondition(Boolean(refreshed.data?.terms_and_condition));
            setSocialMedia(refreshed.data?.social_media || {});
            setOrganization(refreshed.data?.organization || {});
            if (!needsCode) resetPhoneField(refreshed.data?.phone_number);
            await loadPhoneStatus();
            setProfileMessage(
                needsCode
                    ? "Profile updated. Enter the code we sent to verify your new number."
                    : "Profile updated successfully"
            );
        } catch (err: any) {
            setError(err?.response?.data?.detail || err?.response?.data?.message || "Failed to save profile");
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordSave = async () => {
        setError(null);
        setPasswordMessage(null);
        if (!pwdNew || !pwdConfirm) {
            setError("Please enter and confirm your new password");
            return;
        }
        if (pwdNew !== pwdConfirm) {
            setError("Passwords do not match");
            return;
        }
        try {
            setPwdSaving(true);
            await apiClient.put("/account/re/password", {
                password: pwdNew,
                confirm_password: pwdConfirm,
            });
            setPwdCurrent("");
            setPwdNew("");
            setPwdConfirm("");
            setPasswordMessage("Password updated successfully");
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Failed to update password");
        } finally {
            setPwdSaving(false);
        }
    };

    const handleLogoutAllDevices = async () => {
        try {
            await apiClient.post("/auth/logout");
        } catch (err: any) {
            // ignore backend failure and still clear client session
        } finally {
            dispatch(logoutAction());
            navigate("/login", { replace: true });
        }
    };

    const completionPercent = () => {
        const fields = [
            userProfile?.first_name,
            userProfile?.last_name,
            userProfile?.email,
            userProfile?.job_title,
            socialMedia.linkedin_url,
            socialMedia.twitter_url,
            socialMedia.facebook_url,
            socialMedia.instagram_url,
            organization.company_name,
            organization.website_address,
            organization.industry,
            organization.number_of_employees,
            organization.annual_revenue,
            organization.company_address,
            organization.state,
            organization.city,
            organization.postal_code,
        ];
        const total = fields.length;
        const filled = fields.filter((v) => (v || "").toString().trim().length > 0).length;
        if (!total) return 0;
        return Math.round((filled / total) * 100);
    };

    const formattedLastUpdated = () => {
        if (!userProfile?.last_profile_update) return "—";
        const date = new Date(userProfile.last_profile_update);
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString(undefined, {
            day: "numeric",
            year: "numeric",
            month: "short",
        });
    };

    const formattedStatus = () => {
        if (!userProfile?.account_status) return "—";
        const status = userProfile.account_status;
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    return (
        <>
            {phoneModal && (
                <PhoneVerificationModal
                    purpose={phoneModal.purpose}
                    initialPhone={phoneModal.phone}
                    autoSend={!!phoneModal.phone}
                    onClose={() => {
                        setPhoneModal(null);
                        loadPhoneStatus();
                    }}
                    onVerified={async () => {
                        setPhoneModal(null);
                        await refreshProfilePhone();
                        setProfileMessage("Phone number verified.");
                    }}
                />
            )}
            <div className="dashboard-page">
                <h1>Your Profile</h1>
                <p>Manage your personal information and account details</p>
            </div>

            <div className="row">
                <div className="col-md-10">
                    <div className="my-account-container">
                        {/* Tabs Header */}
                        <div className="my-account-tabs">
                            <button
                                className={`my-account-tab ${activeTab === "profile" ? "active" : ""}`}
                                onClick={() => setActiveTab("profile")}
                            >
                                Account Details
                            </button>

                            <button
                                className={`my-account-tab ${activeTab === "security" ? "active" : ""}`}
                                onClick={() => setActiveTab("security")}
                            >
                                Security Settings
                            </button>
                        </div>

                    </div>
                    <div className="my-accounts-box">

                        <div className="my-account-content">
                            {isLoading && (
                                <div className="text-center py-4">
                                    <p className="mt-2">Loading</p>
                                </div>
                            )}
                            {error && (
                                <div className="alert alert-danger">
                                    {error}
                                </div>
                            )}
                            {profileMessage && (
                                <div className="alert alert-success">
                                    {profileMessage}
                                </div>
                            )}
                            {!isLoading && !error && activeTab === "profile" && (
                                <div className="my-account-panel">
                                    <div className="recent-account-box">
                                        <p className="recent-account">Profile Information</p>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">First Name</label>
                                                    <input 
                                                        type="text" 
                                                        className="account-input" 
                                                        value={profileForm.first_name || ""} 
                                                        onChange={(e) => setProfileForm((prev) => ({ ...prev, first_name: e.target.value }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Last Name</label>
                                                    <input 
                                                        type="text" 
                                                        className="account-input" 
                                                        value={profileForm.last_name || ""} 
                                                        onChange={(e) => setProfileForm((prev) => ({ ...prev, last_name: e.target.value }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Job Title (Optional)</label>
                                                    <input 
                                                        type="text" 
                                                        className="account-input" 
                                                        value={profileForm.job_title || ""}
                                                        onChange={(e) => setProfileForm((prev) => ({ ...prev, job_title: e.target.value }))}
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Business Email Address</label>
                                                    <input 
                                                        type="email" 
                                                        className="account-input" 
                                                        value={userProfile?.email || ""} 
                                                        readOnly 
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="account-phone" className="account-label account-phone-label">
                                                        Phone Number
                                                        {userProfile?.phone_number && phoneStatus?.phone_verified && !phoneDirty && (
                                                            <span className="phone-badge is-verified">Verified</span>
                                                        )}
                                                        {userProfile?.phone_number && phoneStatus && !phoneStatus.phone_verified && !phoneDirty && (
                                                            <span className="phone-badge">Not verified</span>
                                                        )}
                                                    </label>
                                                    <div className="account-phone">
                                                        <CountryCodeSelect
                                                            value={phoneCountry}
                                                            onChange={(code) => {
                                                                setPhoneCountry(code);
                                                                setPhoneDirty(true);
                                                            }}
                                                        />
                                                        <input
                                                            id="account-phone"
                                                            type="tel"
                                                            inputMode="tel"
                                                            className="account-input"
                                                            placeholder={maskedExample(phoneCountry)}
                                                            value={formatNational(phoneCountry, phoneNational)}
                                                            onChange={(e) => {
                                                                setPhoneNational(e.target.value.replace(/\D/g, "").slice(0, 15));
                                                                setPhoneDirty(true);
                                                            }}
                                                        />
                                                    </div>
                                                    {phoneStatus?.pending_phone_number && (
                                                        <p className="phone-pending">
                                                            Pending verification: {formatPhone(phoneStatus.pending_phone_number)}{" "}
                                                            <button type="button" onClick={() => setPhoneModal({ purpose: "change" })}>Verify</button>
                                                            <span aria-hidden="true">&middot;</span>
                                                            <button type="button" onClick={discardPendingPhone}>Discard</button>
                                                        </p>
                                                    )}
                                                    {!phoneStatus?.pending_phone_number && phoneStatus?.applies && !phoneStatus.phone_verified && !phoneDirty && userProfile?.phone_number && (
                                                        <p className="phone-pending">
                                                            <button type="button" onClick={() => setPhoneModal({ purpose: "gate" })}>Verify this number</button>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <p className="recent-account">Social Media (Optional)</p>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="profile-field">
                                                    <label className="profile-label">LinkedIn</label>

                                                    <div className="profile-input-wrapper">
                                                        <input
                                                            type="text"
                                                            className="profile-input"
                                                            placeholder="LinkedIn"
                                                            value={socialMedia.linkedin_url || ""}
                                                            onChange={(e) => handleSocialChange("linkedin_url", e.target.value)}
                                                        />
                                                        <img
                                                            src="/images/my-linkedin.png"
                                                            alt=""
                                                            className="profile-input-icon"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="profile-field">
                                                    <label className="profile-label">Twitter</label>

                                                    <div className="profile-input-wrapper">
                                                        <input
                                                            type="text"
                                                            className="profile-input"
                                                            placeholder="Twitter"
                                                            value={socialMedia.twitter_url || ""}
                                                            onChange={(e) => handleSocialChange("twitter_url", e.target.value)}
                                                        />
                                                        <img
                                                            src="/images/my-twitter.png"
                                                            alt=""
                                                            className="profile-input-icon"
                                                        />
                                                    </div>
                                                </div>

                                            </div>

                                            <div className="col-md-4">
                                                <div className="profile-field">
                                                    <label className="profile-label">Facebook</label>

                                                    <div className="profile-input-wrapper">
                                                        <input
                                                            type="text"
                                                            className="profile-input"
                                                            placeholder="Facebook"
                                                            value={socialMedia.facebook_url || ""}
                                                            onChange={(e) => handleSocialChange("facebook_url", e.target.value)}
                                                        />
                                                        <img
                                                            src="/images/my-facebook.png"
                                                            alt=""
                                                            className="profile-input-icon"
                                                        />
                                                    </div>
                                                </div>

                                            </div>

                                            <div className="col-md-4">
                                                <div className="profile-field">
                                                    <label className="profile-label">Instagram</label>

                                                    <div className="profile-input-wrapper">
                                                        <input
                                                            type="text"
                                                            className="profile-input"
                                                            placeholder="Instagram"
                                                            value={socialMedia.instagram_url || ""}
                                                            onChange={(e) => handleSocialChange("instagram_url", e.target.value)}
                                                        />
                                                        <img
                                                            src="/images/my-insta.png"
                                                            alt=""
                                                            className="profile-input-icon"
                                                        />
                                                    </div>
                                                </div>
                                            </div>


                                        </div>

                                        <p className="recent-account">Organization Information</p>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Company Name</label>
                                                    <input type="text" className="account-input" placeholder="e.g., Reachstream Inc." value={organization.company_name || ""} onChange={(e) => handleOrgChange("company_name", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Website Address</label>
                                                    <input type="text" className="account-input" placeholder="e.g., www.reachstream.com" value={organization.website_address || ""} onChange={(e) => handleOrgChange("website_address", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Industry</label>
                                                    <input type="text" className="account-input" placeholder="e.g., Information Technology" value={organization.industry || ""} onChange={(e) => handleOrgChange("industry", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Number of Employees</label>
                                                    <input type="text" className="account-input" placeholder="e.g., 250" value={organization.number_of_employees || ""} onChange={(e) => handleOrgChange("number_of_employees", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Annual Revenue</label>
                                                    <input type="text" className="account-input" placeholder="e.g., 10M - 50M" value={organization.annual_revenue || ""} onChange={(e) => handleOrgChange("annual_revenue", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Company Address</label>
                                                    <input type="text" className="account-input" placeholder="Street, Building No., etc." value={organization.company_address || ""} onChange={(e) => handleOrgChange("company_address", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">State</label>
                                                    <input type="text" className="account-input" placeholder="e.g., California" value={organization.state || ""} onChange={(e) => handleOrgChange("state", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">City</label>
                                                    <input type="text" className="account-input" placeholder="e.g., Santa Clara" value={organization.city || ""} onChange={(e) => handleOrgChange("city", e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="col-md-4">
                                                <div className="account-m">
                                                    <label htmlFor="" className="account-label">Postal Code</label>
                                                    <input type="text" className="account-input" placeholder="e.g., 95050" value={organization.postal_code || ""} onChange={(e) => handleOrgChange("postal_code", e.target.value)} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex flex-row mt-3">
                                            <div>
                                                <input
                                                    type="checkbox"
                                                    checked={termsAndCondition}
                                                    onChange={(e) => setTermsAndCondition(e.target.checked)}
                                                />
                                            </div>
                                            <div>
                                                <p className="recive">I agree to receive newsletters and promotional updates from Reachstream</p>
                                            </div>
                                        </div>
                                        <button type="button" className="saved-changes" disabled={saving} onClick={handleSaveProfile}>{saving ? "Saving..." : "Save Changes"}</button>
                                    </div>

                                </div>
                            )}

                            {activeTab === "security" && (
                                <div className="my-account-panel">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p className="change-password">Change Password</p>
                                            <div className="row">
                                                {/*
                                                <div className="col-md-6">
                                                    <label htmlFor="" className="current-password">Current Password</label>
                                                </div>

                                                <div className="col-md-6">
                                                    <input type="password" className="current-password" placeholder="Enter your current password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} />
                                                </div>
                                                */}

                                                <div className="col-md-6">
                                                    <label htmlFor="" className="current-password">New Password</label>
                                                </div>

                                                <div className="col-md-6">
                                                    <input type="password" className="current-password" placeholder="Enter your new password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} />
                                                </div>

                                                <div className="col-md-6">
                                                    <label htmlFor="" className="current-password">Confirm Password</label>
                                                </div>

                                                <div className="col-md-6">
                                                    <input type="password" className="current-password" placeholder="Enter your confirmed password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} />
                                                </div>
                                            </div>

                                            <div className="d-flex flex-row">
                                                <div>
                                                    <input type="checkbox" onChange={handleLogoutAllDevices} />
                                                </div>

                                                <div>
                                                    <p className="log-me-off">Log me out of all devices</p>
                                                </div>
                                            </div>

                                            <button className="save" type="button" disabled={pwdSaving || !pwdNew || !pwdConfirm} onClick={handlePasswordSave}>{pwdSaving ? "Saving..." : "Save"}</button>
                                            {passwordMessage && <p className="text-success mt-2">{passwordMessage}</p>}

                                        </div>
                                        <div className="col-md-6">
                                            <div className="recent-box">
                                                <p className="recent">Recent Security Activity</p>
                                                {securityActivityLoading ? (
                                                    <p className="mt-2">Loading</p>
                                                ) : securityActivities.length > 0 ? (
                                                    securityActivities.map((activity, index) => (
                                                        <div className="row" key={index}>
                                                            <div className="col-md-4">
                                                                <p className="follow-date">{activity.date}</p>
                                                            </div>
                                                            <div className="col-md-8">
                                                                <p className="logged-in">{activity.activity} ({activity.device}) – {activity.location}</p>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="mt-2">No security activity found</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            )}
                        </div>


                    </div>
                </div>
                <div className="col-md-2">
                    <div className="user-management-box-1">
                        <div className="profile-image-wrapper">
                            <img
                                src={userProfile?.profile_image_url || "/images/popup-img.png"}
                                alt=""
                                className="profile-main-img"
                                style={{
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    width: "108px",
                                    height: "108px",
                                }}
                            />
                            <img
                                src="/images/pro-side.png"
                                alt=""
                                className="profile-badge-img"
                                onClick={() => setOpen(true)}
                            />
                        </div>

                        {open && (
                            <div
                                className="profile-modal-overlay"
                                onClick={() => setOpen(false)}
                            >
                                <div
                                    className={`profile-modal ${dragActive ? "drag-active" : ""}`}
                                    ref={popupRef}
                                    onClick={(e) => e.stopPropagation()}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        setDragActive(true);
                                    }}
                                    onDragLeave={() => setDragActive(false)}
                                    onDrop={handleDrop}
                                >
                                    <div className="d-flex flex-row justify-content-between">
                                        <div>
                                            <p className="my-updated-profile">Update Your Profile Picture</p>
                                        </div>
                                        <div onClick={() => setOpen(false)} style={{ cursor: "pointer" }}>
                                            <img src="/images/cancel.png" alt="" />
                                        </div>
                                    </div>

                                    <div
                                        className="drop-zone"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <div className="d-flex flex-row align-items-center">
                                            <div>
                                                <img
                                                    src={previewImage()}
                                                    width="108"
                                                    height="108"
                                                    style={{ borderRadius: "50%", objectFit: "cover" }}
                                                    alt="preview"
                                                />
                                            </div>

                                            <div className="ms-3">
                                                <p className="drag-it">Drag & drop or <span className="click-it">click to upload</span></p>
                                                <p className="drag-them">(JPG or PNG, up to 5MB)</p>
                                            </div>
                                        </div>
                                    </div>

                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        hidden
                                        accept="image/*"
                                        onChange={(e) => {
                                            const files = e.target.files;
                                            if (!files?.length) return;
                                            handleFile(files[0]);
                                        }}
                                    />

                                    <div className="popup-actions">
                                        <button
                                            type="button"
                                            className="remove-bttn"
                                            disabled={imageUploading || (!selectedFile && !userProfile?.profile_image_url)}
                                            style={
                                                !imageUploading && !selectedFile && !userProfile?.profile_image_url
                                                    ? { color: "#C2C8CE", backgroundColor: "#FFFFFF", borderColor: "#C2C8CE" }
                                                    : undefined
                                            }

                                            onClick={handleRemoveImage}
                                        >
                                            Remove Picture
                                        </button>

                                        <button
                                            type="button"
                                            className="save-bttn"
                                            disabled={!selectedFile || imageUploading}
                                            style={
                                                !imageUploading && !selectedFile
                                                    ? { color: "#fff", backgroundColor: "#C2C8CE", borderColor: "#C2C8CE" }
                                                    : undefined
                                            }

                                            onClick={handleSave}
                                        >
                                            {imageUploading ? "Uploading" : "Save Changes"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="devider"></div>
                        <p className="total-credits">
                            Account Status
                        </p>
                        <span className="total-credits-m">
                            {isLoading ? "Loading" : formattedStatus()}

                        </span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Last Updated
                        </p>
                        <span className="total-credits-r">
                            {isLoading ? "Loading" : formattedLastUpdated()}

                        </span>

                        <div className="devider"></div>

                        <p className="total-credits">
                            Profile Complete
                        </p>
                        <span className="total-credits">
                            {isLoading ? "Loading" : `${completionPercent()}%`}

                        </span>

                    </div>
                </div>
            </div >



        </>
    )
}
export default MyAccount